import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

// Form validation schemas
const loginSchema = z.object({
	email: z.string().email({ message: "Please enter a valid email." }),
	password: z
		.string()
		.min(6, { message: "Password must be at least 6 characters." }),
});

const signupSchema = z.object({
	name: z.string().min(2, { message: "Name must be at least 2 characters." }),
	email: z.string().email({ message: "Please enter a valid email." }),
	password: z
		.string()
		.min(6, { message: "Password must be at least 6 characters." }),
	accountType: z.enum(["retailer", "supplier"]),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type SignupFormValues = z.infer<typeof signupSchema>;

export function AuthModal() {
	const [searchParams] = useSearchParams();
	const defaultTab = searchParams.get("signup") ? "signup" : "login";
	const [currentTab, setCurrentTab] = useState(defaultTab);
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { toast } = useToast();
	const navigate = useNavigate();

	// Login form
	const loginForm = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	// Signup form
	const signupForm = useForm<SignupFormValues>({
		resolver: zodResolver(signupSchema),
		defaultValues: {
			name: "",
			email: "",
			password: "",
			accountType: "supplier",
		},
	});

	// Handle login submit
	const onLoginSubmit = async (data: LoginFormValues) => {
		try {
			setIsLoading(true);

			const { error } = await supabase.auth.signInWithPassword({
				email: data.email,
				password: data.password,
			});

			if (error) throw error;

			toast({
				title: "Login successful",
				description: "Welcome back to Starch.",
			});

			setTimeout(() => {
				navigate("/");
			}, 500);
		} catch (error: any) {
			toast({
				title: "Login failed",
				description:
					error.message ||
					"Please check your credentials and try again.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Handle signup submit
	const onSignupSubmit = async (data: SignupFormValues) => {
		try {
			setIsLoading(true);

			const { error } = await supabase.auth.signUp({
				email: data.email,
				password: data.password,
				options: {
					data: {
						name: data.name,
						accountType: data.accountType,
					},
				},
			});

			if (error) throw error;

			toast({
				title: "Account created",
				description:
					"Your account has been created successfully. Please check your email for verification.",
			});

			setCurrentTab("login"); // Add this line to switch to login tab
		} catch (error: any) {
			toast({
				title: "Signup failed",
				description:
					error.message ||
					"Please check your information and try again.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Handle social auth
	const handleGoogleSignIn = async () => {
		try {
			setIsLoading(true);
			const { error } = await supabase.auth.signInWithOAuth({
				provider: "google",
				options: {
					redirectTo: `${window.location.origin}/auth/callback`,
				},
			});

			if (error) throw error;
		} catch (error: any) {
			toast({
				title: "Authentication failed",
				description: error.message || "Could not sign in with Google.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="max-w-md w-full mx-auto px-6 py-8">
			<div className="mb-8 text-center">
				<h1 className="text-2xl font-display font-semibold tracking-tight mb-2">
					{currentTab === "login" ? "Login to Starch" : "Join Starch"}
				</h1>
				<p className="text-gray-500 text-sm">
					{currentTab === "login"
						? "Enter your credentials to access your account"
						: "Create an account to start your wholesale journey"}
				</p>
			</div>

			<Tabs
				defaultValue={defaultTab}
				value={currentTab}
				onValueChange={setCurrentTab}
				className="w-full">
				<TabsList className="grid grid-cols-2 mb-6">
					<TabsTrigger value="login">Login</TabsTrigger>
					<TabsTrigger value="signup">Signup</TabsTrigger>
				</TabsList>

				<TabsContent
					value="login"
					className="space-y-4 animate-fade-in">
					<Form {...loginForm}>
						<form
							onSubmit={loginForm.handleSubmit(onLoginSubmit)}
							className="space-y-4">
							<FormField
								control={loginForm.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												placeholder="your@email.com"
												type="email"
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={loginForm.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<div className="relative">
												<Input
													placeholder="••••••••"
													type={
														showPassword
															? "text"
															: "password"
													}
													{...field}
													disabled={isLoading}
												/>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() =>
														setShowPassword(
															!showPassword
														)
													}
													className="absolute right-0 top-0 h-full px-3 py-2"
													disabled={isLoading}>
													{showPassword ? (
														<EyeOff className="h-4 w-4" />
													) : (
														<Eye className="h-4 w-4" />
													)}
												</Button>
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="text-right">
								<Button
									variant="link"
									className="p-0 h-auto font-normal text-sm text-gray-500 hover:text-gray-800"
									disabled={isLoading}>
									Forgot password?
								</Button>
							</div>

							<Button
								type="submit"
								variant="accent"
								className="w-full"
								disabled={isLoading}>
								{isLoading ? "Logging in..." : "Login"}
							</Button>
						</form>
					</Form>
				</TabsContent>

				<TabsContent
					value="signup"
					className="space-y-4 animate-fade-in">
					<Form {...signupForm}>
						<form
							onSubmit={signupForm.handleSubmit(onSignupSubmit)}
							className="space-y-4">
							<FormField
								control={signupForm.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Full Name</FormLabel>
										<FormControl>
											<Input
												placeholder="John Doe"
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={signupForm.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												placeholder="your@email.com"
												type="email"
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={signupForm.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<div className="relative">
												<Input
													placeholder="••••••••"
													type={
														showPassword
															? "text"
															: "password"
													}
													{...field}
													disabled={isLoading}
												/>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() =>
														setShowPassword(
															!showPassword
														)
													}
													disabled={isLoading}>
													{showPassword ? (
														<EyeOff className="h-4 w-4" />
													) : (
														<Eye className="h-4 w-4" />
													)}
												</Button>
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={signupForm.control}
								name="accountType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											I am a: <br />
											<span className="text-xs">
												(only supplier onboarding is
												available currently)
											</span>
										</FormLabel>
										<div className="grid grid-cols-2 gap-4 pt-2">
											<span className="w-full">
												<Button
													type="button"
													variant="outline"
													className="w-full justify-center border-gray-200 text-gray-400 cursor-not-allowed"
													disabled={true}>
													Retailer
												</Button>
											</span>
											<Button
												type="button"
												variant="default"
												onClick={() =>
													field.onChange("supplier")
												}
												className="w-full justify-center"
												disabled={isLoading}>
												Supplier
											</Button>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<Button
								type="submit"
								variant="accent"
								className="w-full"
								disabled={isLoading}>
								{isLoading
									? "Creating Account..."
									: "Create Account"}
							</Button>
						</form>
					</Form>
				</TabsContent>
			</Tabs>

			<Separator className="my-8" />

			<div className="space-y-4">
				{currentTab === "login" && (
					<Button
						variant="outline"
						className="w-full flex items-center justify-center gap-2 border-gray-200 hover:bg-gray-50"
						onClick={handleGoogleSignIn}
						disabled={isLoading}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 48 48"
							width="20"
							height="20">
							<path
								fill="#FFC107"
								d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
							/>
							<path
								fill="#FF3D00"
								d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
							/>
							<path
								fill="#4CAF50"
								d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
							/>
							<path
								fill="#1976D2"
								d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
							/>
						</svg>
						Continue with Google
					</Button>
				)}

				<p className="text-xs text-center text-gray-500">
					By creating an account, you agree to our{" "}
					<a href="/terms" className="text-primary hover:underline">
						Terms of Service
					</a>{" "}
					and{" "}
					<a href="/privacy" className="text-primary hover:underline">
						Privacy Policy
					</a>
					.
				</p>
			</div>
		</div>
	);
}
